import { useCallback, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import Table from 'react-bootstrap/Table';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { columnsData } from './Data/aiQuotationColumns';
import { useAiContext } from '../../context/AiContext';
import usePdfStore from '../../store/PdfStore';
import { NOLEGGIATORI } from 'src/utils/constants';
import { PdfSource } from '../../constants/pdfConstants';

interface AiVehicleResultsProps {
	type: any;
	showModal?: any;
	inZoom?: boolean;
	/** Rende un set di quotazioni diverso da quello in AiContext (confronto fra endpoint). */
	quotationsOverride?: any;
}

const AiVehicleResults: React.FC<AiVehicleResultsProps> = ({
	showModal,
	type,
	inZoom,
	quotationsOverride,
}) => {
	const { setPdfSource } = usePdfStore();

	const downloadPdf = (row) => {
		setPdfSource(PdfSource.Quicko);
		showModal('downloadQuotationPdf', row);
	};

	const getColumns = () => {
		switch (type) {
			case 'ai_offers':
				return columnsData(
					downloadPdf,
					undefined,
					'ai_offers',
					rowOverrides,
					handleRowOverride,
					inZoom
				).filter(
					(cd) =>
						![
							// 'marca',
							// 'modello',
							'versione_locale',
							'durata',
							'anticipo',
							'km_annui',
							'nr_servizi',
							'prezzo_accessori_no_iva',
							'download',
							quickoHelper.isSegnalatore ? 'percentage' : undefined,
						].includes(cd.id)
				);
			case 'best_offers':
				return columnsData(
					downloadPdf,
					quickoHelper.isSegnalatore,
					'best_offers',
					rowOverrides,
					handleRowOverride,
					inZoom
				).filter(
					(cd) =>
						![
							// 'marca',
							// 'modello',
							'versione_locale',
							'prezzo_accessori_no_iva',
							quickoHelper.isSegnalatore ? 'percentage' : undefined,
						].includes(cd.id)
				);
			default:
				return columnsData(
					downloadPdf,
					undefined,
					undefined,
					rowOverrides,
					handleRowOverride,
					inZoom
				).filter(
					(cd) =>
						![
							'download',
							quickoHelper.isSegnalatore ? 'percentage' : undefined,
						].includes(cd.id)
				);
		}
	};

	const {
		quotations: contextQuotations,
		services,
		showResults,
		error,
		quotReqState,
		quickoHelper,
		setQuotations,
	} = useAiContext();
	const quotations = quotationsOverride ?? contextQuotations;
	const { t } = useTranslation();

	const [rowOverrides, setRowOverrides] = useState<Record<string, any>>({});

	const handleRowOverride = useCallback(
		(row, overrides?: Record<string, any> | null) => {
			setRowOverrides((prev) => {
				if (overrides === null) {
					const next = { ...prev };
					delete next[row.id];
					return next;
				}
				return { ...prev, [row.id]: { ...(prev[row.id] || {}), ...overrides } };
			});
		},
		[]
	);

	const columns = useMemo(() => getColumns(), [rowOverrides]);

	const getSelectedServices = () => {
		let result: string[] = [];

		for (let k in services) {
			if (Array.isArray(services[k]) && services[k].some((s) => s.isChecked)) {
				result.push(k);
			} else {
				if (services[k].isChecked) {
					result.push(k);
				}
			}
		}

		return result;
	};

	const [selectedServices] = useState(getSelectedServices());

	const data = useMemo(
		() =>
			quotations[type].map((q, idx) => {
				if (type === 'similar_offers') {
					let serviceLabel: string | undefined;

					if (q.noleggiatore.toUpperCase() === NOLEGGIATORI.ARVAL_PRIVATI) {
						q.km_annui = quotReqState.totalKm;
					}

					q.serviceList = [];

					// Vettura sostitutiva
					serviceLabel =
						services.vetturaSostitutiva.find(
							(s) => s.value === q['auto_sostitutiva_gruppo'].toLowerCase()
						)?.labelRiepilogo || 'Vettura Sostitutiva: no';

					if (
						(q.services.excluded || []).some((s) =>
							['auto_sostitutiva_YN', 'auto_sostitutiva_gruppo'].includes(s)
						)
					) {
						q.serviceList.push(
							<span className="text-warning">{serviceLabel}</span>
						);
					} else {
						q.serviceList.push(serviceLabel);
					}

					// Furto e incendio
					serviceLabel =
						services.furtoIncendio.find(
							(s) => s.value === q['incendio_furto_scoperto_perc']
						)?.labelRiepilogo || 'Furto e Incendio: no';

					if (
						(q.services.excluded || []).some((s) =>
							['incendio_furto_YN', 'incendio_furto_scoperto_perc'].includes(s)
						)
					) {
						q.serviceList.push(
							<span className="text-warning">{serviceLabel}</span>
						);
					} else {
						q.serviceList.push(serviceLabel);
					}

					// Kasko
					serviceLabel =
						services.assicurazioneKasko.find(
							(s) => s.value === q['kasko_franchigia']
						)?.labelRiepilogo || 'Kasko: no';

					if (
						(q.services.excluded || []).some((s) =>
							['kasko_YN', 'kasko_franchigia'].includes(s)
						)
					) {
						q.serviceList.push(
							<span className="text-warning">{serviceLabel}</span>
						);
					} else {
						q.serviceList.push(serviceLabel);
					}

					// Manutenzione
					serviceLabel =
						q['manutenzione_YN'] === 'SI'
							? services.manutenzione.labelRiepilogo
							: 'Manutenzione: no';

					if (
						(q.services.excluded || []).some((s) =>
							['manutenzione_YN'].includes(s)
						)
					) {
						q.serviceList.push(
							<span className="text-warning">{serviceLabel}</span>
						);
					} else {
						q.serviceList.push(serviceLabel);
					}

					// RC
					serviceLabel =
						services.assicurazioneRC.find(
							(s) => s.value === q['rca_penale_risarcitoria']
						)?.labelRiepilogo || 'RC: no';

					if (
						(q.services.excluded || []).some((s) =>
							['rca_YN', 'rca_massimale', 'rca_penale_risarcitoria'].includes(s)
						)
					) {
						q.serviceList.push(
							<span className="text-warning">{serviceLabel}</span>
						);
					} else {
						q.serviceList.push(serviceLabel);
					}

					// Tutela Legale
					serviceLabel = q['tutela_legale_YN']
						? services.tutelaLegale.labelRiepilogo
						: 'Tutela Legale: no';

					if (
						(q.services.excluded || []).some((s) =>
							['tutela_legale_YN'].includes(s)
						)
					) {
						q.serviceList.push(
							<span className="text-warning">{serviceLabel}</span>
						);
					} else {
						q.serviceList.push(serviceLabel);
					}

					// Gomme
					if (q['servizio_gomme_YN']) {
						let service = [
							services.gomme.find(
								(s) => s.value === q['tipo_gomma'].toLowerCase()
							)?.labelRiepilogo,
						];

						if (q['gomme_illimitate_YN']) {
							service.push('illimitate');
						} else {
							service.push(
								...[
									'Treni gomme estive: ' + q['n_treno_gomme_estive'],
									'Treni gomme invernali: ' + q['n_treno_gomme_invernali'],
								]
							);
						}

						serviceLabel = service.join(', ');
					} else {
						serviceLabel = 'Gomme: no';
					}

					if (
						(q.services.excluded || []).some((s) =>
							[
								'servizio_gomme_YN',
								'gomme_illimitate_YN',
								'gomme_invernali_YN',
								'tipo_gomma',
								'n_treno_gomme_estive',
								'n_treno_gomme_invernali',
							].includes(s)
						)
					) {
						q.serviceList.push(
							<span className="text-warning">{serviceLabel}</span>
						);
					} else {
						q.serviceList.push(serviceLabel);
					}
				} else {
					q.nr_servizi = `${selectedServices.length}/${selectedServices.length}`;
				}

				if (!!rowOverrides[idx]) {
					Object.assign(q, rowOverrides[idx]);
				} else {
					Object.assign(q, rowOverrides[idx]);
					q.updated_canone = q._original_canone ?? q.updated_canone;
					q.updated_commissioni = q._original_commissioni ?? q.updated_commissioni;
				}

				return type === 'best_offers'
					? Object.assign(q, {
							km_annui: quotReqState.totalKm,
							durata: quotReqState.rentalDuration,
							anticipo: quotReqState.advancePayment,
					  })
					: q;
			}),
		[quotations, type, quotReqState, rowOverrides]
	);

	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		useTable({
			columns,
			data,
		});

	return (
		<>
			{error && <div className="error-inference">{error}</div>}
			{showResults && quotations[type].length ? (
				<Card className="p-0">
					<Table
						striped
						borderless
						hover
						responsive
						{...getTableProps()}
						className="vehicle-table m-0"
					>
						<thead className="border-bottom">
							{headerGroups.map((headerGroup) => (
								<tr {...headerGroup.getHeaderGroupProps()}>
									{headerGroup.headers.map((column: any) => (
										<th
											className="text-nowrap"
											{...column.getHeaderProps({
												className: column.className,
											})}
										>
											{column.render('Header')}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody {...getTableBodyProps()}>
							{rows.map((row) => {
								prepareRow(row);
								return (
									<tr {...row.getRowProps()}>
										{row.cells.map((cell) => {
											let cellHighlight;
											switch (cell.column.id) {
												// case 'nr_servizi':
												// 	const nrServices = cell.value.split('/');
												// 	cellHighlight = nrServices[0] !== nrServices[1];
												// 	break;
												case 'durata':
													cellHighlight =
														cell.value !== quotReqState.rentalDuration;
													break;
												case 'versione_locale':
													cellHighlight =
														cell.value !==
														quotReqState.vehicleState?.dati_veicolo
															?.allestimento;
													break;
												case 'km_annui':
													cellHighlight = cell.value !== quotReqState.totalKm;
													break;
												case 'anticipo':
													cellHighlight =
														cell.value !== quotReqState.advancePayment;
													break;
												case 'prezzo_accessori_no_iva':
													cellHighlight =
														cell.value !== quotReqState.optionalTotals;
													break;
											}

											return (
												<td
													className={cellHighlight ? 'highlight' : ''}
													{...cell.getCellProps()}
												>
													{cell.render('Cell')}
												</td>
											);
										})}
									</tr>
								);
							})}
						</tbody>
					</Table>
				</Card>
			) : (
				''
			)}

			{showResults && quotations[type].length <= 0 && (
				<div className="my-2">{t('no_offer_found_selected_vehicle')}</div>
			)}
		</>
	);
};

export default AiVehicleResults;
